import React from 'react';
import { RemittanceIcon, InsuranceIcon, FinancingIcon, ReferralIcon, ReKYCIcon, ColdIcon, WarmIcon, HotIcon } from "./icons";
import { ChevronRight } from 'lucide-react';

/**
 * LeadCard - Mobile card view for lead inventory
 * Displays lead information in a touch-friendly card format
 */
const LeadCard = ({ data, onClick }) => {
  const getStatusStyle = (status) => {
    const styles = {
      0: "bg-gray-100 text-gray-600",
      1: "bg-[#FEFAD8] text-black",
      2: "bg-[#BEC2FF] text-[#262BC6]",
      3: "bg-[#E3F3FF] text-[#00658E]",
      4: "bg-[#E7FFD6] text-[#116D26]",
      5: "bg-[#E7FFD6] text-[#116D26]",
      6: "bg-red-100 text-red-500",
      11: "bg-red-200 text-red-800"
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  const getLeadStatusStyle = (status) => {
    const styles = {
      Active: "bg-[#FEFAD8] text-black",
      Ongoing: "bg-[#FFEEDC] text-[#A96900]",
      Inactive: "bg-[#EEEEEE] text-[#757575]",
      Tanked: "bg-[#757575] text-[#EEEEEE]"
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  const leadSourceLabels = {
    0: "Raw Lead",
    1: "Fresh Lead",
    2: "Profile Completed",
    3: "Meeting Completion",
    4: "Onboarded",
    5: "Activated",
    6: "Rejected",
    11: "Tanked",
  };

  const interestIcon = data.interest_level === "Cold" ? ColdIcon
    : data.interest_level === "Warm" ? WarmIcon
    : data.interest_level === "Hot" ? HotIcon
    : null;

  return (
    <div 
      className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 active:bg-gray-50 transition-colors"
      onClick={onClick}
    >
      {/* Header: Name + ID */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 text-base truncate">
            {data.name}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">ID: {data.lead_id}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
      </div>

      {/* Products */}
      {data.products && data.products.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {data.products.includes('Remittance') && (
            <img src={RemittanceIcon} alt="Remittance" className="w-6 h-6" title="Remittance" />
          )}
          {data.products.includes('Insurance') && (
            <img src={InsuranceIcon} alt="Insurance" className="w-6 h-6" title="Insurance" />
          )}
          {data.products.includes('Financing') && (
            <img src={FinancingIcon} alt="Financing" className="w-6 h-6" title="Financing" />
          )}
          {data.products.includes('Referral') && (
            <img src={ReferralIcon} alt="Referral" className="w-6 h-6" title="Referral" />
          )}
          {data.products.includes('Re-KYC') && (
            <img src={ReKYCIcon} alt="Re-KYC" className="w-6 h-6" title="Re-KYC" />
          )}
        </div>
      )}

      {/* Status Badges */}
      <div className="flex flex-wrap gap-2 mb-3">
        {data.lead_status && (
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getLeadStatusStyle(data.lead_status)}`}>
            {data.lead_status}
          </span>
        )}
        {data.status !== undefined && (
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(data.status)}`}>
            {leadSourceLabels[data.status] || 'Unknown'}
          </span>
        )}
        {interestIcon && (
          <img src={interestIcon} alt={data.interest_level} className="w-6 h-6" title={data.interest_level} />
        )}
      </div>

      {/* Details */}
      <div className="space-y-2 text-sm">
        {data.lead_source && (
          <div className="flex justify-between">
            <span className="text-gray-500">Source:</span>
            <span className="text-gray-900 font-medium">{data.lead_source}</span>
          </div>
        )}
        {data.last_interacted && (
          <div className="flex justify-between">
            <span className="text-gray-500">Last Contacted:</span>
            <span className="text-gray-900">{data.last_interacted}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCard;

